import { useEffect, useMemo, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { ApiError, api, keys, type ServerSound, type Uuid } from "@shared/api";
import { playSound, preloadSound } from "@shared/media";
import { Hint } from "@shared/ui/bits";
import {
  IconChevronDown,
  IconChevronRight,
  IconPlay,
  IconPrivateChannel,
  IconRecent,
  IconSearch,
  IconSound,
  IconSoundboard,
  IconStar,
} from "@shared/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { ServerTile } from "@entities/server";

const FAVORITES_KEY = "airel.soundboard.favorites";
const RECENT_KEY = "airel.soundboard.recent";
/** «Недавние» — последние сыгранные, как «Часто используемые» в Discord. */
const RECENT_MAX = 6;
/**
 * Перезарядка между звуками. Сервер (`PlaySoundHandler.Cooldown`) держит 400 мс от
 * прихода запроса, мы — 700 от нажатия: запас на сеть, чтобы плитка не открылась
 * раньше, чем сервер примет звук.
 */
const COOLDOWN_MS = 700;

function readIds(key: string): Uuid[] {
  try {
    const list: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(list) ? list.filter((item): item is Uuid => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function writeIds(key: string, ids: Uuid[]) {
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Хранилище закрыто — избранное просто не переживёт перезапуск.
  }
}

interface Section {
  key: string;
  title: string;
  icon: React.ReactNode;
  sounds: ServerSound[];
}

/**
 * Звуковая панель в ряду звонка — как в Discord: поиск, «Избранное»,
 * «Недавние» и звуки по серверам, слева — столбец, который ведёт к разделу.
 * Играть можно звуки всех своих серверов, не только сервера звонка, — как
 * «внешние звуки» в Discord, с этим правом в звонке; без него чужие заперты.
 * Под курсором у плитки три кнопки, как в Discord: послушать у себя, сыграть
 * всем и звезда избранного.
 *
 * Играет сервер, а не мы: он проверяет, что мы в этом звонке и можем
 * говорить, и рассылает событие — у себя звук слышим тем же событием, что и
 * остальные, поэтому и громкость, и задержка у всех одинаковые.
 */
export function SoundboardButton({
  serverId,
  servers,
  channelId,
  canPlay,
  canExternal,
}: {
  serverId: Uuid;
  /** Свои серверы — разделы панели; сервер звонка идёт первым. */
  servers: readonly { id: Uuid; name: string; icon_id: Uuid | null }[];
  channelId: Uuid;
  /** Права говорить и «звуковая панель» в канале звонка: без них сервер звук не пустит. */
  canPlay: boolean;
  /** Право «внешние звуки»: звуки других своих серверов. */
  canExternal: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [shut, setShut] = useState<Set<string>>(new Set());
  const [favorites, setFavorites] = useState<Uuid[]>(() => readIds(FAVORITES_KEY));
  const [recent, setRecent] = useState<Uuid[]>(() => readIds(RECENT_KEY));
  const [problem, setProblem] = useState<string | null>(null);
  /*
   * Перезарядка, как у умения в MMO: плитки не нажимаются, и по ним идёт тень
   * по кругу. Ключ перезапускает анимацию, срок — сколько ей идти.
   */
  const [cool, setCool] = useState<{ key: number; ms: number } | null>(null);
  const coolTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(coolTimer.current), []);

  function coolFor(ms: number) {
    clearTimeout(coolTimer.current);
    setCool({ key: Date.now(), ms });
    coolTimer.current = setTimeout(() => setCool(null), ms);
  }
  const body = useRef<HTMLDivElement>(null);

  const ordered = useMemo(
    () => [...servers.filter((server) => server.id === serverId), ...servers.filter((server) => server.id !== serverId)],
    [servers, serverId],
  );
  const lists = useQueries({
    queries: ordered.map((server) => ({
      queryKey: keys.sounds(server.id),
      enabled: open,
      queryFn: () => api.sounds.list(server.id).then((value) => value.items),
    })),
  });
  const own = lists[0]?.data;

  // Открыли панель — звуки своего сервера разбираются заранее: первое нажатие не ждёт сеть.
  if (open && own) for (const sound of own) preloadSound(sound.file_id);

  const needle = query.trim().toLowerCase();
  const all = lists.flatMap((list) => list.data ?? []);
  const byId = new Map(all.map((sound) => [sound.id, sound]));
  const match = (sound: ServerSound) =>
    !needle || sound.name.toLowerCase().includes(needle) || (sound.emoji ?? "").includes(needle);
  const pick = (ids: Uuid[]) => ids.map((id) => byId.get(id)).filter((sound): sound is ServerSound => !!sound);

  const sections: Section[] = [
    { key: "favorites", title: "Избранное", icon: <IconStar size={14} />, sounds: pick(favorites).filter(match) },
    { key: "recent", title: "Недавние", icon: <IconRecent size={14} />, sounds: pick(recent).filter(match) },
    ...ordered.map((server, index) => ({
      key: server.id,
      title: server.name,
      icon: <ServerTile id={server.id} name={server.name} iconId={server.icon_id} size={16} />,
      sounds: (lists[index]?.data ?? []).filter(match),
    })),
  ].filter((section) => section.sounds.length > 0);

  function toggleFavorite(soundId: Uuid) {
    const next = favorites.includes(soundId) ? favorites.filter((id) => id !== soundId) : [...favorites, soundId];
    setFavorites(next);
    writeIds(FAVORITES_KEY, next);
  }

  async function play(sound: ServerSound) {
    if (cool) return;
    setProblem(null);
    // Перезарядка — сразу по нажатию, не дожидаясь ответа: второе нажатие в это
    // время сервер всё равно отклонит.
    coolFor(COOLDOWN_MS);
    try {
      await api.sounds.play(channelId, sound.id);
      const next = [sound.id, ...recent.filter((id) => id !== sound.id)].slice(0, RECENT_MAX);
      setRecent(next);
      writeIds(RECENT_KEY, next);
    } catch (error) {
      // Отказ по перезарядке (звук из другого окна) — без плашки: плитка и так её показывает.
      if (error instanceof ApiError && error.code === "sound_cooldown") return;
      setProblem(error instanceof ApiError ? error.message : "Звук не сыгрался");
    }
  }

  function toggleSection(key: string) {
    setShut((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const loading = open && lists.length > 0 && lists[0]?.isPending;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Hint label={canPlay ? "Звуковая панель" : "Звуковая панель — нет права говорить"}>
        <PopoverTrigger asChild>
          <button type="button" className={`dock-act ${open ? "on" : ""}`} disabled={!canPlay}>
            <IconSoundboard size={15} />
          </button>
        </PopoverTrigger>
      </Hint>
      <PopoverContent side="top" align="end" sideOffset={8} aria-label="Звуковая панель">
        <div className="soundboard soundpad">
          <label className="soundpad-search">
            <IconSearch size={14} />
            <input
              autoFocus
              value={query}
              placeholder="Найдите подходящий звук"
              aria-label="Поиск звука"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <div className="soundpad-main">
            <nav className="soundpad-rail" aria-label="Разделы">
              {sections.map((section) => (
                <Hint key={section.key} label={section.title}>
                  <button
                    type="button"
                    className="soundpad-jump"
                    onClick={() =>
                      body.current
                        ?.querySelector(`[data-section="${section.key}"]`)
                        ?.scrollIntoView({ block: "start", behavior: "smooth" })
                    }
                  >
                    {section.icon}
                  </button>
                </Hint>
              ))}
            </nav>
            <div className="soundpad-body" ref={body}>
              {loading && <div className="soundboard-note">Загружаем…</div>}
              {!loading && sections.length === 0 && (
                <div className="soundboard-note">
                  {needle
                    ? "Ничего не нашлось"
                    : "На серверах пока нет звуков. Их добавляют в настройках сервера, раздел «Звуковая панель»."}
                </div>
              )}
              {sections.map((section) => {
                const closed = shut.has(section.key);
                return (
                  <section key={section.key} data-section={section.key}>
                    <button type="button" className="soundpad-head" onClick={() => toggleSection(section.key)}>
                      {section.icon}
                      <span className="ellipsis">{section.title}</span>
                      {closed ? <IconChevronRight size={12} /> : <IconChevronDown size={12} />}
                    </button>
                    {!closed && (
                      <div className="soundpad-grid">
                        {section.sounds.map((sound) => {
                          const locked = sound.server_id !== serverId && !canExternal;
                          const favorite = favorites.includes(sound.id);
                          return (
                            <div
                              key={sound.id}
                              className={`soundpad-sound${locked ? " locked" : ""}${cool && !locked ? " cooling" : ""}`}
                              // Щелчок по плитке мимо кнопок — тоже «сыграть всем».
                              onClick={() => !locked && void play(sound)}
                            >
                              <span className="soundpad-face">
                                {sound.emoji && <span className="soundboard-emoji">{sound.emoji}</span>}
                                <span className="ellipsis">{sound.name}</span>
                                {favorite && <IconStar size={11} className="soundpad-star" fill="currentColor" />}
                              </span>
                              <span className="soundpad-acts" onClick={(event) => event.stopPropagation()}>
                                <Hint label="Послушать у себя">
                                  <button
                                    type="button"
                                    className="soundpad-act"
                                    aria-label={`Послушать «${sound.name}» у себя`}
                                    onClick={() => playSound(sound.file_id, sound.volume)}
                                  >
                                    <IconSound size={14} />
                                  </button>
                                </Hint>
                                <Hint label={locked ? `${sound.name} · нет права «Использовать внешние звуки»` : sound.name}>
                                  <button
                                    type="button"
                                    className="soundpad-act soundpad-act-play"
                                    aria-label={`Сыграть «${sound.name}»`}
                                    disabled={locked || !!cool}
                                    onClick={() => void play(sound)}
                                  >
                                    {locked ? <IconPrivateChannel size={14} /> : <IconPlay size={14} fill="currentColor" />}
                                  </button>
                                </Hint>
                                <Hint label={favorite ? "Убрать из избранного" : "В избранное"}>
                                  <button
                                    type="button"
                                    className={`soundpad-act${favorite ? " on" : ""}`}
                                    aria-label={favorite ? "Убрать из избранного" : "В избранное"}
                                    aria-pressed={favorite}
                                    onClick={() => toggleFavorite(sound.id)}
                                  >
                                    <IconStar size={14} fill={favorite ? "currentColor" : "none"} />
                                  </button>
                                </Hint>
                              </span>
                              {cool && !locked && (
                                <span
                                  key={cool.key}
                                  className="soundpad-cool"
                                  style={{ animationDuration: `${cool.ms}ms` }}
                                  aria-hidden
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
          {problem && <div className="soundboard-note danger-text">{problem}</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
